import { useMergeRefs } from "@floating-ui/react";
import { forwardRef, useMemo, useRef } from "react";

import LockIcon from "@assets/icons/lock-closed.svg";

import { cn } from "@utils/tailwind.util";
import { getRelativeTime } from "@utils/date.util";
import { getUserInitialsFromFullName } from "@utils/users.util";
import { DEFAULT_NOTE_TITLE } from "@config/constants";
import { openNotePage } from "@utils/navigation.util";
import type { AINote } from "@app-types/notes.type";
import { OrganizationLogo } from "@ui/Logos";
import { Tag } from "@components/Tags/Tag";
import { RenderAvatar } from "@ui/Avatars";
import { Tooltip } from "@ui/Tooltip";
import { View } from "@ui/View";

export type INoteProps = {
  note: AINote;
  showTitle?: boolean;
  showTags?: boolean;
};

const MAX_VISIBLE_TAGS = 1;

const NoteTags = ({ tags }: { tags: AINote["related_items"] }) => {
  const remainingTags = tags.length - MAX_VISIBLE_TAGS;

  return (
    <ul className="flex flex-nowrap items-center gap-1 text-content-primary dark:text-invertedPrimary80">
      {tags.slice(0, MAX_VISIBLE_TAGS)?.map((tag) => (
        <Tag key={tag.object.id} data={tag} isDisabled />
      ))}

      {remainingTags > 0 && (
        <Tooltip
          offset={0}
          placement="bottom"
          className="flex leading-[0] dark:border-gray-500"
          tooltipContentClassName="p-2 max-h-[300px] overflow-auto dark:bg-gray-600"
          tooltipContent={
            <ul className="flex flex-col gap-1 text-left">
              {tags.slice(MAX_VISIBLE_TAGS).map((tag) => (
                <Tag key={tag.object.id} data={tag} isDisabled />
              ))}
            </ul>
          }
        >
          <li
            className={cn(
              "inline-flex h-7 min-w-[32px] flex-none flex-shrink-0 items-center justify-center gap-1 rounded-md bg-background-tag px-2 align-middle leading-none no-underline outline-none ring-1 ring-inset ring-stroke-primary transition-all hover:bg-gray-200",
              "dark:bg-gray-600 dark:ring-gray-600 dark:group-hover:bg-gray-700"
            )}
          >
            +{remainingTags}
          </li>
        </Tooltip>
      )}
    </ul>
  );
};

export const Note = forwardRef<HTMLDivElement, INoteProps>(
  ({ note, showTitle = false, showTags = false }, ref) => {
    const innerRef = useRef<HTMLDivElement>(null);
    const refs = useMergeRefs([ref, innerRef]);
    const lastEditBy = note.last_editor || note.user;
    const initials = getUserInitialsFromFullName(lastEditBy?.full_name || "Unknown");
    const noteTitle = note.title || DEFAULT_NOTE_TITLE;

    const lastEdited = useMemo(() => {
      return getRelativeTime(note.modified);
    }, [note.modified]);

    const totalVisibleTags = useMemo(() => {
      const relatedItemsVisible = (note.related_items || []).length;

      return relatedItemsVisible;
    }, [note.related_items]);

    const handleOpenNoteInNewTab = () => openNotePage(note.id);

    return (
      <article
        ref={refs}
        className={cn(
          "group/container relative cursor-pointer flex h-full flex-col space-y-2.5 rounded-lg border border-stroke-primary bg-background-primary p-3",
          showTitle ? "min-h-[176px]" : "min-h-[156px]",
          "dark:border-gray-600 dark:bg-gray-700"
        )}
        onClick={handleOpenNoteInNewTab}
      >
        <View.Condition if={showTitle}>
          <header>
            <h1
              className="mr-7 line-clamp-1 text-sm font-medium leading-4 text-content-primary dark:text-white"
              title={noteTitle}
            >
              {noteTitle}
            </h1>
          </header>
        </View.Condition>

        <main className="flex flex-1 flex-col space-y-2.5">
          <p
            className={cn(
              "line-clamp-4 flex-1 text-xs leading-4 text-content-secondary mask-image to-bottom mask-strength-0",
              "dark:text-invertedPrimary80",
              { "line-clamp-2": totalVisibleTags > 0 }
            )}
            style={{
              WebkitMaskImage: "linear-gradient(to bottom, black 60%, transparent 100%)",
              maskImage: "linear-gradient(to bottom, black 60%, transparent 100%)",
              WebkitMaskSize: "100% 100%",
              maskSize: "100% 100%",
              WebkitMaskRepeat: "no-repeat",
              maskRepeat: "no-repeat",
            }}
          >
            {note.description}
          </p>

          <View.Condition if={showTags}>
            {note.related_items?.length > 0 && <NoteTags tags={note.related_items} />}
            {note.items && note.items?.length > 0 && <NoteTags tags={note.items} />}
          </View.Condition>
        </main>

        <footer className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <RenderAvatar
              className="mr-1 inline-flex h-7 w-7 align-middle"
              avatarUrl={lastEditBy?.avatar_url}
              text={initials}
            />

            <span className="inline-block align-middle text-xs font-normal text-content-secondary">
              Edited {lastEdited || "Unknown"}
            </span>
          </div>

          <div>
            {note.access === "Private" ? (
              <LockIcon width={20} height={20} className="text-content-secondary" />
            ) : (
              <OrganizationLogo />
            )}
          </div>
        </footer>
      </article>
    );
  }
);
